// Single-use invitation to create a partner account.
//
// Flow: an Eighty Days admin creates the `partners/{id}` record first, then
// issues an invite from the partner edit page. The invite link is
//
//     {siteUrl}/invite/{inviteId}?t={secret}
//
// where `secret` is generated server-side and only its sha256 is stored, so a
// leaked database dump yields no usable invites. The plaintext secret is
// returned exactly once — to the admin who created the invite — which is why
// the admin UI offers "regenerate" rather than "copy link again".
//
// There are two ways that link reaches the person it is for, and the difference
// is only in who does the sending: the server emails it (the usual case), or it
// is handed back to the admin to send however they like. Either way it is the
// same single-use, address-locked link.
//
// The collection is closed to all client reads and writes; every operation goes
// through a Cloud Function, including the pre-sign-in preview.

import type { PartnerInvitePrefillType } from './types_partner';
import type { PartnerRoleType } from './types_partnerUser';

// Firestore collection name.
export const coll_invites = 'invites';

export type InviteStatusType = 'pending' | 'redeemed' | 'revoked';

/**
 * How the invitation link was handed over.
 *
 * 'email' — the server sent it to the invitee. Nobody else ever sees the link.
 * 'link'  — the plaintext link was returned to the admin to pass on themselves,
 *           for the times email is the wrong channel (a partner that filters us,
 *           an address still being confirmed, a manager standing at the desk).
 */
export type InviteDeliveryType = 'email' | 'link';

export const inviteStatusLabels: Record<InviteStatusType, string> = {
  pending: 'Pending',
  redeemed: 'Redeemed',
  revoked: 'Revoked',
};

export interface InviteType {
  id: string; // random doc id — public, and not the secret
  partnerId: string;
  partnerName: string; // denormalised so the accept screen can name the partner
  role: PartnerRoleType;

  // The invite is locked to this address: redemption requires the signed-in
  // account's email to match exactly, so a forwarded link is useless.
  email: string; // lowercased

  tokenHash: string; // sha256(secret), hex — the secret itself is never stored

  status: InviteStatusType;
  expiresAt: string; // ISO

  createdAt: string;
  createdByUid: string;
  createdByEmail: string;

  // How the link left the server. Recorded because it changes what to do about
  // an invitation nobody has used: chase an email, or re-send a link that may
  // never have been passed on. An email that failed to send is stored as 'link'
  // with `sendError` set — that is what actually happened.
  delivery: InviteDeliveryType;
  sentAt?: string; // ISO — when SendGrid accepted it. 'email' delivery only.
  sendError?: string; // why the email did not go out, if it was asked for

  redeemedAt?: string;
  redeemedByUid?: string;

  revokedAt?: string;
  revokedByUid?: string;
}

/** Default lifetime of a new invitation link. */
export const INVITE_DEFAULT_EXPIRY_DAYS = 14;

/**
 * Whether a still-pending invitation has run out of time.
 *
 * Nothing sweeps Firestore for expired links, so one that has lapsed keeps
 * `status: 'pending'` until somebody revokes or regenerates it. Redemption
 * catches this server-side (`checkInvite`); this is the read-time equivalent,
 * so a list can say "expired" instead of implying the link still works.
 */
export function isInviteExpired(invite: InviteType): boolean {
  return new Date(invite.expiresAt).getTime() <= Date.now();
}

/**
 * Why an invite cannot be redeemed, or 'ok'. Returned by the `invitePreview`
 * callable so the accept page can explain itself before the user signs in.
 */
export type InviteCheckResultType = 'ok' | 'not-found' | 'expired' | 'revoked' | 'already-redeemed';

export const inviteCheckMessages: Record<Exclude<InviteCheckResultType, 'ok'>, string> = {
  'not-found': 'This invitation link is not valid. Please ask Eighty Days for a new one.',
  // Both of these are what a re-issued invitation does to the link it replaced,
  // and re-issuing is the common reason to be seeing them: resending revokes
  // the old link, and an expired one is usually followed by a fresh email. So
  // the newer invitation is named first, before asking anyone to chase us.
  expired:
    'This invitation link has expired. If a newer invitation was sent to you, please use that one. Otherwise, ask Eighty Days for a new link.',
  revoked:
    'This invitation link is no longer valid. If a newer invitation was sent to you, please use that one. Otherwise, ask Eighty Days for a new link.',
  'already-redeemed': 'This invitation link has already been used. Try signing in instead.',
};

/** Response of the `invitePreview` callable — never includes the token hash. */
export interface InvitePreviewType {
  result: InviteCheckResultType;
  partnerId?: string;
  partnerName?: string;
  email?: string;
  role?: PartnerRoleType;

  // What the partner record already says about them, so a manager confirms
  // it rather than retyping it. Manager invitations only — a staff invite has no
  // form to fill, so it is told nothing it has no use for. See
  // `pickPartnerInvitePrefill` for what this may and may not contain.
  partnerPrefill?: PartnerInvitePrefillType;
}

/** Fields a caller supplies when creating an invite. */
export interface CreateInviteRequestType {
  partnerId: string;
  email: string;
  role: PartnerRoleType;
  expiryDays?: number;
  /** When set, this invite replaces (revokes) an existing one. */
  replacesInviteId?: string;
  /** Who sends the link. Defaults to 'email' — the server sends it. */
  delivery?: InviteDeliveryType;
}

/**
 * What happened to the new invitation.
 *
 * `delivered` is what the server actually did, not what was asked for: a
 * requested email that SendGrid refused comes back as 'link' with `url` and
 * `sendError` set, so the admin can pass on a link that is perfectly valid
 * rather than be told only that something failed.
 *
 * When `delivered` is 'link', `url` is the one and only time the plaintext
 * link exists outside the server.
 */
export interface CreateInviteResponseType {
  inviteId: string;
  email: string;
  delivered: InviteDeliveryType;
  url?: string;
  sendError?: string;
}

/**
 * Builds the invitation URL. Used by the Cloud Function when creating an
 * invite, and by the accept page when parsing one.
 */
export function buildInviteUrl(siteUrl: string, inviteId: string, token: string): string {
  return `${siteUrl.replace(/\/$/, '')}/invite/${inviteId}?t=${token}`;
}
